import React, { useState, useEffect } from 'react';
import { Heart, X, MapPin, Clock, Star, Filter, Zap, Target, Users, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import EnhancedButton from './EnhancedButton';
import { InteractiveRating } from './InteractiveComponents';
import { LoadingSpinner } from './LoadingStates';

// Smart Match Card with Swipe Actions
export const SmartMatchCard = ({ match, onLike, onPass, onSuperLike }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const defaultMatch = {
    id: 1,
    name: "Sarah Chen",
    age: 28,
    distance: "2 miles away",
    photo: "https://images.unsplash.com/photo-1569913486515-b74bf7751574?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhc2lhbiUyMHdvbWFuJTIwaGVhZHNob3R8ZW58MHx8fHwxNzU0MTMwODM1fDA&ixlib=rb-4.1.0&q=85",
    bio: "Adventure seeker who loves hiking and trying new restaurants. Looking for someone to explore the city with!",
    interests: ["Hiking", "Photography", "Coffee", "Travel", "Yoga"],
    compatibility: 87,
    mutualFriends: 3,
    lastActive: "Active 2 hours ago",
    verified: true,
    photos: [
      "https://images.unsplash.com/photo-1569913486515-b74bf7751574?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhc2lhbiUyMHdvbWFuJTIwaGVhZHNob3R8ZW58MHx8fHwxNzU0MTMwODM1fDA&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1581065178047-8ee15951ede6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NTQxMzA4NDR8MA&ixlib=rb-4.1.0&q=85",
      "https://images.unsplash.com/photo-1589525231707-f2de2428f59c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxhc2lhbiUyMHdvbWFuJTIwaGVhZHNob3R8ZW58MHx8fHwxNzU0MTMwODM1fDA&ixlib=rb-4.1.0&q=85"
    ]
  };

  const user = match || defaultMatch;
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  const handleAction = (action) => {
    setIsVisible(false);
    setTimeout(() => {
      if (action === 'like' && onLike) onLike(user);
      if (action === 'pass' && onPass) onPass(user);
      if (action === 'superlike' && onSuperLike) onSuperLike(user);
    }, 300);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % user.photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + user.photos.length) % user.photos.length);
  };

  if (!isVisible) return null;

  return (
    <div className="relative max-w-sm mx-auto animate-scaleIn">
      <div 
        className={`bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 hover-lift ${
          isDragging ? 'cursor-grabbing scale-105' : 'cursor-grab'
        }`}
        style={{
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) rotate(${dragOffset.x * 0.1}deg)`,
        }}
      >
        {/* Photo Section */}
        <div className="relative h-96 overflow-hidden group">
          <img
            src={user.photos[currentPhotoIndex]}
            alt={user.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            onError={(e) => {
              e.target.style.display = 'none';
              // Show a fallback or retry
              console.log('Image failed to load:', user.photos[currentPhotoIndex]);
            }}
            onLoad={() => {
              // Image loaded successfully
              console.log('Image loaded successfully:', user.photos[currentPhotoIndex]);
            }}
          />
          
          {/* Photo Navigation */}
          <div className="absolute inset-x-0 top-4 flex justify-center space-x-1">
            {user.photos.map((_, index) => (
              <div
                key={index}
                className={`w-1 h-8 rounded-full transition-all duration-300 ${
                  index === currentPhotoIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>

          {/* Photo Controls */}
          <button
            onClick={prevPhoto}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <button
            onClick={nextPhoto}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-black/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Compatibility Badge */}
          <div className="absolute top-4 left-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-sm font-semibold animate-pulse">
            {user.compatibility}% Match
          </div>

          {/* Verification Badge */}
          {user.verified && (
            <div className="absolute top-4 right-4 bg-blue-500 text-white p-1 rounded-full animate-bounce">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
          )}

          {/* Gradient Overlay */}
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/70 to-transparent" />
        </div>

        {/* Profile Info */}
        <div className="absolute bottom-0 left-0 right-0 text-white p-6">
          <div className="flex items-end justify-between mb-2">
            <div>
              <h3 className="text-2xl font-bold">{user.name}, {user.age}</h3>
              <div className="flex items-center space-x-1 text-white/80">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">{user.distance}</span>
              </div>
            </div>
            
            {user.mutualFriends > 0 && (
              <div className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full text-xs">
                {user.mutualFriends} mutual friends
              </div>
            )}
          </div>

          <p className="text-white/90 text-sm mb-3 line-clamp-2">{user.bio}</p>

          {/* Interests */}
          <div className="flex flex-wrap gap-1 mb-3">
            {user.interests.slice(0, 3).map((interest, index) => (
              <span
                key={interest}
                className="bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full animate-fadeIn"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {interest}
              </span>
            ))}
            {user.interests.length > 3 && (
              <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                +{user.interests.length - 3} more
              </span>
            )}
          </div>

          <div className="text-xs text-white/70">{user.lastActive}</div>
        </div>

        {/* Action Buttons */}
        <div className="absolute bottom-6 right-6 flex space-x-2">
          <button
            onClick={() => handleAction('pass')}
            className="w-12 h-12 bg-gray-500/80 backdrop-blur-sm hover:bg-gray-600 text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 animate-bounce"
            style={{ animationDelay: '0.1s' }}
          >
            <X className="w-6 h-6" />
          </button>
          
          <button
            onClick={() => handleAction('superlike')}
            className="w-12 h-12 bg-blue-500/80 backdrop-blur-sm hover:bg-blue-600 text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 animate-bounce"
            style={{ animationDelay: '0.2s' }}
          >
            <Star className="w-6 h-6" />
          </button>
          
          <button
            onClick={() => handleAction('like')}
            className="w-12 h-12 bg-rose-500/80 backdrop-blur-sm hover:bg-rose-600 text-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 animate-bounce"
            style={{ animationDelay: '0.3s' }}
          >
            <Heart className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Advanced Matching Filters
export const AdvancedFilters = ({ filters = {}, onUpdateFilters, onApply }) => {
  const [localFilters, setLocalFilters] = useState({
    ageRange: [22, 35],
    distance: 25,
    interests: [],
    education: '',
    lifestyle: [],
    dealBreakers: [],
    ...filters
  });

  const [isOpen, setIsOpen] = useState(false);

  const interestOptions = [
    "Travel", "Fitness", "Cooking", "Photography", "Music", "Art", "Books",
    "Movies", "Gaming", "Sports", "Dancing", "Hiking", "Yoga", "Wine"
  ];

  const educationOptions = [
    "High School", "Some College", "Bachelor's Degree", "Master's Degree", "PhD", "Trade School"
  ];

  const lifestyleOptions = [
    "Non-smoker", "Social Drinker", "Pet Lover", "Fitness Enthusiast", 
    "Foodie", "Early Bird", "Night Owl", "Homebody", "Adventurer"
  ];

  const updateFilter = (key, value) => {
    const updated = { ...localFilters, [key]: value };
    setLocalFilters(updated);
  };

  const applyFilters = () => {
    onUpdateFilters && onUpdateFilters(localFilters);
    onApply && onApply(localFilters);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <EnhancedButton
        onClick={() => setIsOpen(!isOpen)}
        variant="outline"
        icon={<Filter className="w-5 h-5" />}
        className="relative"
      >
        Filters
        {Object.keys(filters).length > 0 && (
          <span className="absolute -top-2 -right-2 w-5 h-5 bg-rose-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
            {Object.keys(filters).length}
          </span>
        )}
      </EnhancedButton>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 p-6 z-50 animate-slideInRight">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Age Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Age Range: {localFilters.ageRange[0]} - {localFilters.ageRange[1]}
              </label>
              <div className="flex items-center space-x-4">
                <input
                  type="range"
                  min="18"
                  max="65"
                  value={localFilters.ageRange[0]}
                  onChange={(e) => updateFilter('ageRange', [parseInt(e.target.value), localFilters.ageRange[1]])}
                  className="flex-1"
                />
                <input
                  type="range"
                  min="18"
                  max="65"
                  value={localFilters.ageRange[1]}
                  onChange={(e) => updateFilter('ageRange', [localFilters.ageRange[0], parseInt(e.target.value)])}
                  className="flex-1"
                />
              </div>
            </div>

            {/* Distance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Distance: {localFilters.distance} miles
              </label>
              <input
                type="range"
                min="1"
                max="100"
                value={localFilters.distance}
                onChange={(e) => updateFilter('distance', parseInt(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Interests */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Interests</label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {interestOptions.map((interest) => (
                  <button
                    key={interest}
                    onClick={() => {
                      const updated = localFilters.interests.includes(interest)
                        ? localFilters.interests.filter(i => i !== interest)
                        : [...localFilters.interests, interest];
                      updateFilter('interests', updated);
                    }}
                    className={`px-3 py-1 text-sm rounded-full transition-all duration-300 ${
                      localFilters.interests.includes(interest)
                        ? 'bg-rose-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {interest}
                  </button>
                ))}
              </div>
            </div>

            {/* Education */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Education</label>
              <select
                value={localFilters.education}
                onChange={(e) => updateFilter('education', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
              >
                <option value="">Any</option>
                {educationOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            {/* Lifestyle */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Lifestyle</label>
              <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                {lifestyleOptions.map((lifestyle) => (
                  <button
                    key={lifestyle}
                    onClick={() => {
                      const updated = localFilters.lifestyle.includes(lifestyle)
                        ? localFilters.lifestyle.filter(l => l !== lifestyle)
                        : [...localFilters.lifestyle, lifestyle];
                      updateFilter('lifestyle', updated);
                    }}
                    className={`px-3 py-1 text-xs rounded-full transition-all duration-300 ${
                      localFilters.lifestyle.includes(lifestyle)
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {lifestyle}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 mt-6 pt-6 border-t border-gray-200">
            <EnhancedButton
              onClick={() => {
                setLocalFilters({
                  ageRange: [22, 35],
                  distance: 25,
                  interests: [],
                  education: '',
                  lifestyle: [],
                  dealBreakers: []
                });
              }}
              variant="ghost"
              size="sm"
              className="flex-1"
            >
              Clear All
            </EnhancedButton>
            <EnhancedButton
              onClick={applyFilters}
              size="sm"
              className="flex-1"
              icon={<Target className="w-4 h-4" />}
            >
              Apply
            </EnhancedButton>
          </div>
        </div>
      )}
    </div>
  );
};

// Match Suggestions Feed
export const MatchSuggestionsFeed = ({ suggestions = [], onRefresh, onViewProfile }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clickedCard, setClickedCard] = useState(null);

  const mockSuggestions = [
    {
      id: 1,
      type: "personality_match",
      title: "Perfect Personality Match",
      description: "Sarah has a 94% personality compatibility with you",
      user: {
        name: "Sarah",
        age: 28,
        photo: "https://images.unsplash.com/photo-1569913486515-b74bf7751574?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxhc2lhbiUyMHdvbWFuJTIwaGVhZHNob3R8ZW58MHx8fHwxNzU0MTMwODM1fDA&ixlib=rb-4.1.0&q=85",
        compatibility: 94
      },
      reason: "Both extroverted with shared love for adventure"
    },
    {
      id: 2,
      type: "interest_match",
      title: "Shared Interests",
      description: "You both love hiking, photography, and craft coffee",
      user: {
        name: "Emma",
        age: 26,
        photo: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=200&h=200&fit=crop",
        compatibility: 89
      },
      reason: "5 shared interests including rare hobby matches"
    },
    {
      id: 3,
      type: "location_match",
      title: "Nearby & Available",
      description: "Lives 0.8 miles away and was active 10 minutes ago",
      user: {
        name: "Zoe",
        age: 30,
        photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop",
        compatibility: 82
      },
      reason: "Close proximity with high activity level"
    }
  ];

  const displaySuggestions = suggestions.length > 0 ? suggestions : mockSuggestions;

  const refreshSuggestions = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call
    onRefresh && onRefresh();
    setLoading(false);
  };

  const getSuggestionIcon = (type) => {
    switch (type) {
      case 'personality_match': return <Sparkles className="w-5 h-5 text-purple-500" />;
      case 'interest_match': return <Heart className="w-5 h-5 text-rose-500" />;
      case 'location_match': return <MapPin className="w-5 h-5 text-blue-500" />;
      default: return <Star className="w-5 h-5 text-yellow-500" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <LoadingSpinner text="Finding your perfect matches..." size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 animate-fadeInUp">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
          <Zap className="w-6 h-6 text-yellow-500" />
          <span>Smart Suggestions</span>
        </h3>
        <EnhancedButton
          onClick={refreshSuggestions}
          variant="outline"
          size="sm"
          loading={loading}
          icon={<Target className="w-4 h-4" />}
        >
          Refresh
        </EnhancedButton>
      </div>

      <div className="space-y-4">
        {displaySuggestions.map((suggestion, index) => (
          <div
            key={suggestion.id}
            className={`p-4 rounded-xl border transition-all duration-300 hover-lift animate-slideInLeft cursor-pointer touch-manipulation ${
              clickedCard === suggestion.id 
                ? 'border-rose-400 bg-rose-50 shadow-lg' 
                : 'border-gray-200 hover:border-rose-300'
            }`}
            style={{ animationDelay: `${index * 0.1}s` }}
            onClick={() => {
              setClickedCard(suggestion.id);
              setTimeout(() => setClickedCard(null), 1000);
              onViewProfile && onViewProfile(suggestion.user);
            }}
          >
            <div className="flex items-start space-x-4">
              <div className="p-2 bg-gray-100 rounded-lg">
                {getSuggestionIcon(suggestion.type)}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-800">{suggestion.title}</h4>
                  <div className="text-sm font-bold text-green-600">
                    {suggestion.user.compatibility}% Match
                  </div>
                </div>
                
                <p className="text-gray-600 text-sm mb-3">{suggestion.description}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <img
                      src={suggestion.user.photo}
                      alt={suggestion.user.name}
                      className="w-10 h-10 rounded-full border-2 border-gray-200"
                    />
                    <div>
                      <div className="font-medium text-gray-800">
                        {suggestion.user.name}, {suggestion.user.age}
                      </div>
                      <div className="text-xs text-gray-500">{suggestion.reason}</div>
                    </div>
                  </div>
                  
                  <EnhancedButton 
                    size="sm"
                    className="px-4 py-2 text-sm font-medium min-h-[44px] touch-manipulation"
                    icon={<Heart className="w-4 h-4" />}
                    onClick={() => onViewProfile && onViewProfile(suggestion.user)}
                  >
                    View Profile
                  </EnhancedButton>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {displaySuggestions.length === 0 && (
        <div className="text-center py-8">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-600 mb-2">No suggestions yet</h4>
          <p className="text-gray-500 mb-4">Complete your profile to get better match suggestions</p>
          <EnhancedButton onClick={refreshSuggestions} icon={<Sparkles className="w-5 h-5" />}>
            Find Matches
          </EnhancedButton>
        </div>
      )}
    </div>
  );
};

export default {
  SmartMatchCard,
  AdvancedFilters,
  MatchSuggestionsFeed
};